import { useEffect, useRef, useState } from 'react';
import { type UserRole } from '../types/gateway';
import { SWITCHABLE_ROLES, getRoleConfig } from '../config/roles';
import styles from './RoleSwitcher.module.css';

interface RoleSwitcherProps {
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}

export function RoleSwitcher({ currentRole, onRoleChange }: RoleSwitcherProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const active = getRoleConfig(currentRole);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (role: UserRole) => {
    onRoleChange(role);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef} data-accent={active.accent}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.triggerIcon}>{active.icon}</span>
        <span className={styles.triggerLabel}>{active.label}</span>
        <span className={styles.caret} aria-hidden>{open ? '\u25B2' : '\u25BC'}</span>
      </button>

      {open && (
        <div className={styles.menu} role="listbox">
          <div className={styles.menuHeader}>You are working as</div>
          {SWITCHABLE_ROLES.map((role) => {
            const isActive = role.key === currentRole;
            return (
              <button
                key={role.key}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`${styles.option} ${isActive ? styles.optionActive : ''}`}
                data-accent={role.accent}
                onClick={() => handleSelect(role.key)}
              >
                <span className={styles.optionIcon}>{role.icon}</span>
                <span className={styles.optionText}>
                  <span className={styles.optionLabel}>{role.label}</span>
                  <span className={styles.optionDesc}>{role.description}</span>
                </span>
                {isActive && <span className={styles.check} aria-hidden>{'\u2713'}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
